import { Button, DotsVerticalIcon, Dropdown, DropdownMenu, Table, TableColumns } from '@bp/ui-components';
import { Assignment, useAssignments } from 'components/Assignments/hooks/useAssignments';
import { useRequireParams } from 'hooks/useRequireParams';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../../utils/dateCalculations';
import { BpLink } from '../../BpLink/BpLink';

type AssignmentsPreviewTableProps = {
  teachingUnitUuid: string;
  loading: boolean;
  onNavigate: (uuid: string) => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  isGroupEditor: boolean;
};

export const AssignmentsPreviewTable: FC<AssignmentsPreviewTableProps> = ({
  teachingUnitUuid,
  loading,
  onNavigate,
  onEdit,
  onDelete,
  isGroupEditor,
}) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { assignments, activeAssignments } = useAssignments({ courseUuid, teachingUnitUuid });

  const columns: TableColumns<Assignment>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <BpLink
          value={row.original.title}
          onNavigate={() => onNavigate(row.original.uuid)}
          isEditorOfCourse={isGroupEditor}
        />
      ),
      canExpand: true,
    },
    {
      id: 'dates',
      accessorKey: 'dates',
      cell: ({ row }) => {
        const isActive = activeAssignments.map((a) => a.uuid).includes(row.original.uuid);

        return (
          <div style={{ color: isActive ? 'var(--color-success)' : undefined }}>{`${niceDate(
            row.original.visibleFrom,
            'short',
          )} - ${niceDate(row.original.dueDate, 'short')}`}</div>
        );
      },
      size: 130,
    },
  ];

  const data: Assignment[] = [
    ...activeAssignments,
    ...assignments.filter((a) => !activeAssignments.map((a) => a.uuid).includes(a.uuid)).reverse(),
  ];

  function getMenu(uuid: string): ReactNode {
    return (
      <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon className='small' />} />}>
        <DropdownMenu
          data={[
            {
              label: t('common.edit'),
              onClick: () => onEdit(uuid),
            },
            { type: 'ruler' },
            {
              label: t('delete.delete'),
              color: 'error',
              onClick: () => onDelete(uuid),
            },
          ]}
        />
      </Dropdown>
    );
  }

  return (
    <Table<Assignment>
      loading={loading}
      maxHeight={220}
      className='hide-gutter'
      canScroll
      hideHeader
      customPadding='var(--spacing-6)'
      customRowHeight='var(--list-row-height)'
      customLastColSpacing='var(--spacing-3)'
      isDesktop={true}
      data={data}
      columns={columns}
      emptyStateSettings={{
        hideIcon: true,
        size: 'small',
        title: '',
        subtitle: t('assignments.noAssignments'),
        forcedHeight: '150px',
      }}
      lastCol={({ original }) => getMenu(original.uuid)}
      lastColWidth='24px'
    />
  );
};
